import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { SectionCard } from '../components/sectionCard/SectionCard';
import { StateChip } from '../components/stateChip/StateChip';
import { DodTimeseriesSeries } from 'api/dod/dod.v2.types';
import { BarPanel } from '../components/barPanel/BarPanel';
import { BarDatum } from '../components/barPanel/BarPanel.types';
import { TimeSeriesPanel } from '../components/timeSeriesPanel/TimeSeriesPanel';

import { ReconnectTimeline } from './ReconnectTimeline';
import * as Styled from './WorkerTab.styles';
import {
  WORKER_CIRCUIT_MAX_FAILURES,
  WorkerConnection,
  WorkerEvent,
  ageMsSince,
  certDaysLeft,
  eventsForConnection,
  connectionIdentityParts,
  formatDurationMs,
  isCertExpiringSoon,
  workerCircuitState,
  workerConnectionState,
} from './worker.helpers';

export type WorkerConnectionCardProps = {
  connection: WorkerConnection;
  events: WorkerEvent[];
  now: number;
  timelineFrom: number;
  timelineTo: number;
  /** Per-server command rate. Empty when the metric cannot be attributed to one RCP. */
  commandSeries: DodTimeseriesSeries[];
  commandSeriesLabels: Record<string, string>;
  commandBars: BarDatum[];
  commandsP95Label: string;
  isCommandsLoading: boolean;
  /** false when nothing on this deployment can split commands per server. */
  hasPerServerCommands: boolean;
};

/**
 * One card per RCP server the worker holds a session to.
 *
 * Only what the heartbeat carries is rendered: the TLS version, handshake time and certificate
 * common name appear once the worker publishes them, and are dropped from the identity line when
 * it does not, rather than printed as a row of dashes.
 */
export const WorkerConnectionCard = ({
  connection,
  events,
  now,
  timelineFrom,
  timelineTo,
  commandSeries,
  commandSeriesLabels,
  commandBars,
  commandsP95Label,
  isCommandsLoading,
  hasPerServerCommands,
}: WorkerConnectionCardProps): JSX.Element => {
  const { formatMessage } = useLocale();

  const sessionAgeMs = ageMsSince(connection.connectedSince, now);
  const daysLeft = certDaysLeft(connection, now);
  const certWarning = isCertExpiringSoon(daysLeft);
  const connectionEvents = eventsForConnection(events, connection.rcpId);

  return (
    <SectionCard>
      <Styled.CardStack data-testid={`dod-worker-connection-${connection.rcpId}`}>
        <Styled.ConnectionHeader>
          <StateChip state={workerConnectionState(connection.state)} />

          <Styled.ConnectionIdentity>
            <Styled.ConnectionName>{connection.rcpName || connection.rcpId}</Styled.ConnectionName>
            <Styled.MutedMono>
              {connectionIdentityParts(
                connection,
                sessionAgeMs === null
                  ? null
                  : formatMessage(
                      { id: AppMessages['dod.worker.sessionAge'] },
                      { age: formatDurationMs(sessionAgeMs) },
                    ),
              ).join(' · ')}
            </Styled.MutedMono>
          </Styled.ConnectionIdentity>

          <Styled.Spacer />

          <Styled.ChipRow>
            <StateChip
              state={workerCircuitState(connection.circuit)}
              label={formatMessage({ id: AppMessages['dod.worker.circuit'] })}
              caption={formatMessage(
                { id: AppMessages['dod.worker.circuitFailures'] },
                { count: connection.consecutiveFailures, max: WORKER_CIRCUIT_MAX_FAILURES },
              )}
            />

            <StateChip
              tone="neutral"
              testId={`dod-worker-heartbeat-${connection.rcpId}`}
              text={formatMessage(
                { id: AppMessages['dod.worker.heartbeatAge'] },
                { age: formatDurationMs(connection.heartbeatAgeMs) },
              )}
            />

            <StateChip
              tone={certWarning ? 'warn' : 'neutral'}
              testId={`dod-worker-cert-${connection.rcpId}`}
              text={
                daysLeft === null
                  ? formatMessage({ id: AppMessages['dod.worker.certUnknown'] })
                  : certWarning
                  ? formatMessage({ id: AppMessages['dod.worker.certSoon'] }, { days: daysLeft })
                  : formatMessage(
                      { id: AppMessages['dod.worker.cert'] },
                      {
                        date: connection.certExpiresAt ? new Date(connection.certExpiresAt).toLocaleDateString() : '—',
                        days: daysLeft,
                      },
                    )
              }
            />

            <StateChip
              tone="neutral"
              testId={`dod-worker-reconnects-${connection.rcpId}`}
              text={`${formatMessage(
                { id: AppMessages['dod.worker.reconnectSuccess'] },
                { count: connection.reconnects.success },
              )} · ${formatMessage(
                { id: AppMessages['dod.worker.reconnectFailure'] },
                { count: connection.reconnects.failure },
              )}`}
            />
          </Styled.ChipRow>
        </Styled.ConnectionHeader>

        {connection.lastError && (
          <Styled.MetricDetail>
            {formatMessage({ id: AppMessages['dod.worker.lastError'] })}: {connection.lastError}
          </Styled.MetricDetail>
        )}

        <Styled.PanelGrid>
          {hasPerServerCommands && (
            <TimeSeriesPanel
              title={formatMessage({ id: AppMessages['dod.worker.panel.commands'] })}
              series={commandSeries}
              seriesLabels={commandSeriesLabels}
              area
              height={140}
              isLoading={isCommandsLoading}
              actions={<Styled.ToolbarLabel>{commandsP95Label}</Styled.ToolbarLabel>}
            />
          )}

          <SectionCard title={formatMessage({ id: AppMessages['dod.worker.panel.reconnects'] })} bodyHeight={140}>
            <ReconnectTimeline events={connectionEvents} from={timelineFrom} to={timelineTo} />
          </SectionCard>

          {hasPerServerCommands && (
            <BarPanel
              title={formatMessage({ id: AppMessages['dod.worker.panel.commandsByType'] })}
              data={commandBars}
              layout="horizontal"
              height={140}
              isLoading={isCommandsLoading}
            />
          )}
        </Styled.PanelGrid>
      </Styled.CardStack>
    </SectionCard>
  );
};
